import { memo, useCallback, useState } from 'react';
import {
  Button,
  Grid,
  Typography,
  WidgetContainer,
  useHeroSnackbar,
  useTranslations,
  List,
  RenderIf,
} from '@uniqkey-frontend/shared-app';
import { useNavigate } from 'react-router-dom';
import { useGetGroupLatestOrganizations } from '../../../../../../hooks/reactQuery';
import AddOrganizationToGroupWidgetListItem from
  './components/AddOrganizationToGroupWidgetListItem';
import PageRouteEnum from '../../../../../../enums/PageRouteEnum';
import { GroupModuleTabEnum } from '../../../GroupModule';
import AddOrganizationToGroupWidgetNoData from '../AddOrganizationToGroupWidgetNoData';
import GroupOrganizationsSelectorModal from '../../../GroupOrganizationsSelectorModal';
import ACLEnum from '../../../../../../enums/ACLEnum';
import { useUser } from '../../../../../../contexts/UserContext';

interface IAddOrganizationToGroupWidgetProps {
  groupId: string;
}

const AddOrganizationToGroupWidget = (props: IAddOrganizationToGroupWidgetProps) => {
  const { groupId } = props;

  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const navigate = useNavigate();
  const { userCan } = useUser();

  const {
    data, isLoading, isError,
  } = useGetGroupLatestOrganizations({
    groupId,
    page: 1,
    pageLength: 2,
  }, {
    onError: () => {
      showError({ text: t('common.somethingWentWrong') });
    },
  });

  const [
    isGroupOrganizationsSelectorModalOpen,
    setIsGroupOrganizationsSelectorModalOpen,
  ] = useState(false);
  const handleGroupOrganizationsSelectorModalOpen = useCallback(
    () => setIsGroupOrganizationsSelectorModalOpen(true),
    [],
  );
  const handleGroupOrganizationsSelectorModalClose = useCallback(
    () => setIsGroupOrganizationsSelectorModalOpen(false),
    [],
  );

  const handleNavigation = useCallback(() => {
    navigate({
      pathname: `${PageRouteEnum.Groups}/${groupId}/${GroupModuleTabEnum.Organizations}`,
    });
  }, [groupId, navigate]);

  const { data: preOrganizations } = data ?? {};
  const organizations = preOrganizations ?? [];

  if (isError) {
    return null;
  }

  if (!organizations.length && !isLoading) {
    return (
      <AddOrganizationToGroupWidgetNoData groupId={groupId} isForAdmin={false} />
    );
  }

  return (
    <WidgetContainer
      container
      flexDirection="column"
      py={3}
      withShadow
      isLoading={isLoading}
    >
      <Grid item>
        <Typography variant="h6">{t('addOrganizationToGroupWidget.title')}</Typography>
      </Grid>
      <List>
        {organizations.map((organization) => (
          <AddOrganizationToGroupWidgetListItem
            groupId={groupId}
            organization={organization}
            key={organization.organizationId}
          />
        ))}
      </List>
      <Grid container item columnGap={1}>
        <RenderIf condition={userCan(ACLEnum.GroupAddOrganizations)}>
          <Grid item mt={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleGroupOrganizationsSelectorModalOpen}
            >
              {t('addOrganizationToGroupWidget.addOrganization')}
            </Button>
          </Grid>
        </RenderIf>
        <RenderIf condition={!!organizations.length}>
          <Grid item mt={2}>
            <Button variant="outlined" fullWidth onClick={handleNavigation}>
              {t('addOrganizationToGroupWidget.viewAll')}
            </Button>
          </Grid>
        </RenderIf>
      </Grid>
      {isGroupOrganizationsSelectorModalOpen && (
        <GroupOrganizationsSelectorModal
          isOpen={isGroupOrganizationsSelectorModalOpen}
          onClose={handleGroupOrganizationsSelectorModalClose}
          groupId={groupId}
          isForAdmin={false}
        />
      )}
    </WidgetContainer>
  );
};

export default memo(AddOrganizationToGroupWidget);
